"use client";

import { useEffect, useRef, useState } from "react";
import { styles } from "./ScrollCards.style";
import { ScrollCard } from "./components/ScrollCard";
import { colors } from "../../core";

export const ScrollCardColor = {
  YellowGreen: colors.voltGreen.value,
  LightGreen: colors.limeGreen.value,
  BabyBlue: colors.babyBlue.value,
  Gray: colors.dusk.value,
} as const;

export type ScrollCardColorType = (typeof ScrollCardColor)[keyof typeof ScrollCardColor];

const STACK_TOP_PX = 64;
const CARD_INDEXES = [0, 1, 2, 3] as const;

export interface ScrollCardItem {
  heading?: string;
  text?: string;
  color: ScrollCardColorType;
}

export interface ScrollCardsProps {
  cards: ScrollCardItem[];
}

export const ScrollCards = (props: ScrollCardsProps) => {
  const { cards } = props;
  const [maxHeight, setMaxHeight] = useState(0);
  const [gap, setGap] = useState(0);
  const sectionRef = useRef<HTMLElement>(null);
  const cardRefs = useRef<(HTMLDivElement | null)[]>([]);
  const indexes = CARD_INDEXES.slice(0, cards.length);
  const totalCards = indexes.length;

  useEffect(() => {
    const measure = () => {
      setMaxHeight(0);
      requestAnimationFrame(() => {
        const measured = cardRefs.current.map((el) => el?.getBoundingClientRect().height ?? 0);
        const newMax = Math.max(...measured, 0);
        setMaxHeight((prev) => (prev !== newMax ? newMax : prev));

        if (sectionRef.current) {
          const raw = getComputedStyle(sectionRef.current).getPropertyValue("--scroll-gap").trim();
          setGap(parseInt(raw, 10));
        }
      });
    };

    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [cards]);

  return (
    <section ref={sectionRef} className={styles.section()}>
      {indexes.map((i) => {
        const marginBottom = (totalCards - 1 - i) * gap;
        const marginTop = i === 0 ? 0 : gap - (totalCards - i) * gap;

        return (
          <ScrollCard
            key={i}
            ref={(el) => {
              cardRefs.current[i] = el;
            }}
            card={cards[i]}
            index={i}
            top={STACK_TOP_PX + i * gap}
            height={maxHeight || undefined}
            marginBottom={marginBottom}
            marginTop={marginTop}
          />
        );
      })}
    </section>
  );
};
